class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    // Si se vacía el carrito con el cajón abierto, el contenido se repinta sin pasar por open()
    new MutationObserver(() => {
      if (this.classList.contains('active')) this.pintarSugerenciasVacio();
    }).observe(this, { childList: true, subtree: true });
  }

  // Carrito vacío: 3 productos al azar, elegidos aquí y no en Liquid porque la página puede venir de caché.
  // Reutiliza el botón "+" de las recomendaciones del carrito ([data-ivc-cross-add]).
  pintarSugerenciasVacio() {
    const lista = this.querySelector('[data-ivc-vacio-lista]');
    const datos = this.querySelector('[data-ivc-vacio-datos]');
    if (!lista || !datos || lista.children.length) return;
    let productos;
    try {
      productos = JSON.parse(datos.textContent);
    } catch (e) {
      return;
    }
    if (!productos.length) return;
    for (let i = productos.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [productos[i], productos[j]] = [productos[j], productos[i]];
    }
    productos.slice(0, 3).forEach((p) => {
      const fila = document.createElement('li');
      fila.className = 'ivc-cross-item';
      const foto = document.createElement('a');
      foto.className = 'ivc-cross-img';
      foto.href = p.u;
      foto.tabIndex = -1;
      foto.setAttribute('aria-hidden', 'true');
      const img = document.createElement('img');
      img.src = p.i;
      img.alt = '';
      img.width = 120;
      img.height = 120;
      foto.appendChild(img);
      const info = document.createElement('span');
      info.className = 'ivc-cross-info';
      const nombre = document.createElement('a');
      nombre.className = 'ivc-cross-nombre';
      nombre.href = p.u;
      nombre.textContent = p.t;
      const precio = document.createElement('span');
      precio.className = 'ivc-cross-precio';
      precio.textContent = p.p;
      info.append(nombre, precio);
      const boton = document.createElement('button');
      boton.type = 'button';
      boton.className = 'ivc-cross-add';
      boton.setAttribute('data-ivc-cross-add', '');
      boton.setAttribute('data-variant-id', p.v);
      boton.setAttribute('aria-label', `${lista.dataset.add} ${p.t} ${lista.dataset.toCart}`);
      boton.textContent = '+';
      fila.append(foto, info, boton);
      lista.appendChild(fila);
    });
    this.querySelector('[data-ivc-vacio-bloque]').hidden = false;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.pintarSugerenciasVacio();
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    // El carrito no se abre solo al añadir: se repinta y actualiza el contador; se abre desde el icono.
    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
